import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Helmet } from 'react-helmet';
import classNames from 'classnames';
import { get } from 'lodash';
import {
	compose,
	lifecycle,
	withHandlers,
	withProps,
	withPropsOnChange,
	withStateHandlers,
} from 'recompose';

import { STORAGE_DEALS_SEARCH_PATH } from 'config/search';

// components
import { BackToTop } from 'components/core/BackToTop';
import { withPageViewTracking } from 'components/core/library/tracking-utils';
import { Page, Row, Column } from 'components/core/Page';
import { Spinner } from 'components/core/Spinner';
import { PaginationSummary } from 'components/core/Pagination';
import { SearchPagination } from 'components/search/SearchPagination';
import SEOContentVariation from 'components/search/SEOContentVariation/SEOContentVariation';
import StickySearchBar from 'components/organisms/StickySearchBar/StickySearchBar';
import EmbeddedSearchMap from 'components/search/EmbeddedSearchMap/EmbeddedSearchMap';
import FacilityList from 'components/organisms/FacilityList';
import { SearchCTCFooter } from 'components/search/SearchCTCFooter/SearchCTCFooter';
import SortDropDownVariation from 'components/search/SortDropdownVariation/SortDropDownVariation';
import { useDecision } from '@optimizely/react-sdk';
import { OPTIMIZELY_FLAGS } from 'config/optimizely';
import { trackDecision } from 'lib/optimizelyDebug';
import { DealsDropdown } from 'components/molecules';

// Filter components (selected at render time based on Optimizely flag)
import { SearchFilters } from 'components/organisms/SearchFilters';
import { SearchFilterContent } from 'components/search/SearchFilterContent';
import { VehicleSearchFilters } from 'components/search/VehicleSearchFilters';
import { TruckFilterContent } from 'components/search/TruckFilterContent';

// decorators

import withViewportWidth from 'decorators/withViewportWidth';
import {
	withTruckPromos,
	withSearchPageHandlers,
	withAdditionalFacilitiesIndicator,
} from 'decorators/search';
import { withSearchHandler, withWebPageSchema } from 'decorators';

// Redux/Selectors/actions
import { searchSelectorVariation } from 'store/selectors/search';
import mapDispatchPropsToActions from 'store/helpers/mapDispatchToActions';
import {
	fetchSearchResults,
	fetchStorageDealsResults,
	mapRedoSearchAction,
} from 'store/search/actions';
import { fetchBestReviews } from 'store/reviews/actions';
import { getFacilityHoursAndDirection } from 'store/facilities/actions';
import {
	getLocalPhoneNumberByZipCode,
	getLocalPhoneNumberByCityState,
	getTollfreePhoneNumber,
} from 'store/pageMeta/actions';

// Utils
import { buildFacilityUrl } from 'utils/url';
import { isLarge } from 'utils/breakpoints';
import { formatSizeText } from 'utils/format';

// Tracking
import { PAGE_TYPES } from '@sparefoot/tracking-events';

import './searchPageVariation.scss';

import {
	calculateFilterCount,
	nearMeAmenityKeyMap,
	nearMeVehicleKeyMap,
} from '../../utils/filterCount';

import { SearchFiltersTray } from 'components/organisms/SearchFiltersTray';
import { Modal, ModalBody } from 'components/core/Modal';
import { UnitSizeGuide } from 'components/unit/UnitSizeGuide';
import { buildSearchLink, getNearMeHeadline } from 'lib/search';

const sizeBuckets = ['5x10', '10x10', '10x15', '10x20'];

const DEALS_DROPDOWN_OPTIONS = [
	{ value: 'all', display: 'All Deals' },
	{ value: 'fmf', display: 'First Month Free' },
	{ value: 'additional', display: 'Additional Deals' },
];

// Only build the map CSS URL when the build-time hash is available. In
// local/dev environments REACTFOOT_VERSION may be undefined and we should
// avoid adding a broken link tag to the document head.
const hash = process.env.REACTFOOT_VERSION || null;
const cssUrl = hash
	? `https://philes.sparefoot.com/assets/${hash}/Map.css`
	: null;

// Function to dynamically load the CSS (no-op when url is falsy)
// Was created to fix a race condition with SearchMap component
const loadMapCss = (url) => {
	if (!url) return;
	if (typeof window !== 'undefined' && typeof document !== 'undefined') {
		const link = document.createElement('link');
		link.rel = 'stylesheet';
		link.href = url;
		link.onload = () => {
			// keep lightweight logging for debugging without noisy output
			console.info('Map CSS loaded');
		};
		link.onerror = (err) => console.error('Failed to load map css', err);
		document.head.appendChild(link);
	}
};

export const enhance = compose(
	withViewportWidth,
	connect(
		searchSelectorVariation,
		{
			fetchSearchResults,
			fetchStorageDealsResults,
			fetchBestReviews,
			getFacilityHoursAndDirection,
			getLocalPhoneNumberByZipCode,
			getLocalPhoneNumberByCityState,
			getTollfreePhoneNumber,
			mapRedoSearchAction,
		},

		mapDispatchPropsToActions,
	),
	withProps(({ match }) => {
		const nearMeHeadline = getNearMeHeadline(match);
		return nearMeHeadline ? { primaryHeadline: nearMeHeadline } : {};
	}),
	// staticMapUrl prop was previously passed here but is currently unused
	// so we avoid adding an unnecessary HOC to keep the compose chain clear.
	withStateHandlers(
		(props) => ({
			filtersShowing: false,
			mapShowing: false,
			showHoursForFacility: null,
			unitSizeGuideShowing: false,
			preselectedFacilityId: null,
			mobileView: 'list',
			isSpinnerVisible: Boolean(props.loading),
			selectedMapFacilityId: null,
		}),
		{
			openFilters: () => () => ({ filtersShowing: true }),
			closeFilters: () => () => ({ filtersShowing: false }),
			toggleFilters: (state) => () => ({
				filtersShowing: !state.filtersShowing,
			}),
			toggleMobileView: (state) => () => {
				// need to scroll to the top of the page when toggling the mobile view
				typeof window !== 'undefined' &&
					window.scrollTo({ top: 0, behavior: 'smooth' });
				return {
					mobileView: state.mobileView === 'list' ? 'map' : 'list',
				};
			},
			showSpinner: () => () => ({ isSpinnerVisible: true }),
			hideSpinner: () => () => ({ isSpinnerVisible: false }),
			handleFilterCountChange: () => (count) => ({
				filterCount: count,
			}),
			handleFilterCountReset: () => () => ({ filterCount: 0 }),
			handleHideUnitGuide: () => () => ({ unitSizeGuideShowing: false }),
			handleShowUnitGuide: () => () => ({ unitSizeGuideShowing: true }),
		},
	),
	withPropsOnChange(['searchContent'], ({ searchContent }) => {
		const lowestPrices = get(
			searchContent,
			'pricingStats.lowestPrices',
			{},
		);
		const numericKeys = Object.keys(lowestPrices)
			.filter((k) => /^\d+$/.test(k))
			.sort((a, b) => Number(a) - Number(b));
		const hasNumericKeys = numericKeys.length > 0;
		const buckets = hasNumericKeys ? numericKeys : sizeBuckets;

		const units = buckets.reduce((unit, size) => {
			const lowestPricedUnit = get(searchContent.pricingStats, [
				'lowestPrices',
				size,
			]);
			const averagePrice = get(searchContent.pricingStats, [
				'averagePrices',
				size,
				'price',
			]);

			if (get(lowestPricedUnit, 'price', null) && averagePrice) {
				const sizeText = hasNumericKeys ? `${size}'` : size;
				return [
					...unit,
					{
						...lowestPricedUnit,
						formattedSizeText: hasNumericKeys
							? sizeText
							: formatSizeText(size),
						sizeText,
						averagePrice,
						reserveUrl: buildFacilityUrl(
							lowestPricedUnit.facility,
							lowestPricedUnit.facilityName,
							lowestPricedUnit?.facilityCity ||
								searchContent?.location?.city,
							lowestPricedUnit?.facilityState ||
								searchContent?.location?.state,
						),
					},
				];
			}

			return unit;
		}, []);

		return { units };
	}),
	withPropsOnChange(
		['searchLocation', 'searchParams', 'searchType', 'match', 'pageType'],
		({
			searchParams,
			searchType,
			handleFilterCountChange,
			handleFilterCountReset,
			searchLocation,
			match,
			pageType,
		}) => {
			handleFilterCountReset();
			if (!searchLocation) return;

			const nearMeParams =
				match && pageType === 'storageNearMeLandingPage'
					? {
							storageAmenity: match?.params?.keyword || null,
							vehicle: match?.params?.keyword || null,
						}
					: {};

			const currentCount = calculateFilterCount(
				searchParams,
				searchType,
				nearMeParams,
			);
			handleFilterCountChange(currentCount);
		},
	),
	withHandlers({
		handleToggleFilters: (props) => () => {
			props.filtersShowing ? props.closeFilters() : props.openFilters();
			return { filtersShowing: !props.filtersShowing };
		},
		onSearchSuccess: (props) => () =>
			props.actions.fetchBestReviews(
				props.facilities.map((facility) => facility.id),
			),
		handleSearchParamChange: (props) => (newParams) => {
			props.showSpinner();
			const { match } = props;
			let searchPath =
				match?.params?.keyword === 'storage-deals'
					? STORAGE_DEALS_SEARCH_PATH
					: null;
			props.history.push(
				buildSearchLink(
					{
						...props.searchParams,
						...newParams,
						page: 1,
					},
					searchPath,
				),
			);
			return { queryParams: newParams };
		},
		handleScrollToFacility:
			({ viewportWidth }) =>
			(facilityId) => {
				// Check if viewport is large
				const showMapFacilityCard = !isLarge(viewportWidth);

				if (typeof document !== 'undefined' && !showMapFacilityCard) {
					const element = document.getElementById(
						`facility-${facilityId}`,
					);

					if (element) {
						// Get the sticky search bar height for offset
						const stickyBar = document.querySelector(
							'.sticky-search-bar-variation',
						);
						const offset = stickyBar
							? stickyBar.offsetHeight + 20
							: 100;

						// Get element position relative to viewport
						const elementPosition =
							element.getBoundingClientRect().top;
						const offsetPosition =
							elementPosition + window.pageYOffset - offset;

						// Scroll to the position
						window.scrollTo({
							top: offsetPosition,
							behavior: 'smooth',
						});
					}
				}
			},
	}),
	withSearchPageHandlers,
	lifecycle({
		componentDidMount() {
			// Only attempt to load the remote map stylesheet when a valid URL exists
			loadMapCss(cssUrl);
			this.props.onSearchSuccess();
			// Applying a filter on a /near-me/ page pushes to /search.html, which
			// switches React Router to a different route key and remounts this
			// component fresh. componentWillReceiveProps (in withSearchPageHandlers)
			// only fires on updates, not mounts, so no fetch fires on that first
			// transition. We detect the case here: this component is mounting on
			// the /search route (pathname matches) and the URL still carries
			// subPageType=nearMePages from the near-me navigation.
			if (
				this.props.queryParams?.subPageType === 'nearMePages' &&
				this.props.location?.pathname === '/search.html'
			) {
				this.props.actions
					.fetchSearchResults(this.props.queryParams)
					.then(() => this.props.onSearchSuccess());
			}
		},
		componentDidUpdate(prevProps) {
			// If was loading and now not loading, hide spinner
			if (prevProps.loading && !this.props.loading) {
				this.props.hideSpinner();
			}

			// Inverse logic to show spinner when loading starts or location becomes available
			if (!prevProps.loading && this.props.loading) {
				this.props.showSpinner();
			}
		},
	}),
	withTruckPromos,
	withSearchHandler,
	withAdditionalFacilitiesIndicator,
	withPageViewTracking(PAGE_TYPES.SEARCH),
	withWebPageSchema,
);

export function SearchPageVariation({
	actions,
	handleSearchParamChange, // To be used by sortBy in filter modal
	handleHideUnitGuide,
	// isTruckSerp, // used in SearchFilters/page layout
	// openFilters, // kept to be used by top sticky search bar/footer
	// closeFilters, // kept to be used by top sticky search bar/footer
	toggleFilters,
	filtersShowing, // kept to be used by top sticky search bar/footer
	// filterComponent,
	queryParams,
	urlQueryParams,
	// showHoursForFacility, // SearchResults/SearchRail
	unitSizeGuideShowing,
	// showVehicleLengthTool, // SearchResults
	searchParams,
	viewportWidth,
	searchLocation, // FacilityCard
	searchId, // SearchResults
	searchPath, // SearchResults
	totalFacilities, // SearchMap/SearchResults
	facilities, // FacilityList/FacilityCards etc
	filterCount,
	// locationLabel, // SearchResults
	searchTerm, // Used in FacilityList for tracking
	searchType, // FacilityCard
	page, // SearchResults component
	canonicalUrl, // SearchResults/SearchBreadcrumbs
	cityUrl, // Breadcrumbs/SearchFilterContent
	facilitiesPerPage, // Used in FacilityList
	localPhone, // Used in FacilityCard
	stateUrl, // Used in breadcrumbs
	searchContent, // Seo search content below the map/facility cards
	numPages, // used in OG Facility List/SearchPagination
	phone, // Used in SearchResults/FacilityCard
	handleSearch, // primary search method used in Sticky Search Bar Variation and SearchRail
	faqContent, // used in SearchContent/FAQ
	faqShowing, // used in SearchContent
	firstListingIsVipEligible, // Used in FacilityList to highlight VIP facility
	vipAccount, // Used to determine if the account/facility is a VIP
	vipFacilityId, // Used in FacilityList to highlight VIP facility
	vipFacilityImages, // Used in VIP facility card header
	preselectedFacilityId, // Used on google map
	fmfFacilitiesCount, // Used in OG Facility List
	promoFacilitiesCount, // Used in OG Facility List
	pageType,
	clpPageSchema,
	clpMapSchema,
	nearMeWebpageSchema,
	nearMeWebSiteSchema,
	facilitySelfStorageSchemas,
	baseUrl, // Used in OG Facility List
	loading, // Used to determine if search is loading
	units, // FacilityCards
	mobileView,
	toggleMobileView,
	highlightedFacility, // corresponds with map/highlightFacility action will be used for syncing map/list hovers
	isSpinnerVisible,
	showSpinner,
	hideSpinner,
	withinCallCenterHours,
	handleToggleFilters,
	isTruckSerp,
	isVehicleSerp,
	facetCounts,
	handleShowUnitGuide,
	priceTransparency,
	handleScrollToFacility,
	isStorageDealsPage,
	primaryHeadline,
	prevUrl, // SEO prev pagination URL
	nextUrl, // SEO next pagination URL
	match,
}) {
	const [filtersDecision] = useDecision(OPTIMIZELY_FLAGS.FILTERS_TEST.key);
	trackDecision(OPTIMIZELY_FLAGS.FILTERS_TEST.key, filtersDecision);
	const useNewFilters = filtersDecision?.enabled || false;
	let newNearMeParams = {};
	if (match && pageType === 'storageNearMeLandingPage') {
		const keyword = match?.params?.keyword || null;
		newNearMeParams = {
			storageAmenity:
				keyword && nearMeAmenityKeyMap[keyword] ? keyword : null,
			vehicle: keyword && nearMeVehicleKeyMap[keyword] ? keyword : null,
		};
	}

	// Select filter component based on Optimizely flag
	let filterComponent;
	if (isTruckSerp) {
		filterComponent = <TruckFilterContent />;
	} else if (useNewFilters) {
		filterComponent = (
			<SearchFilters
				facetCounts={facetCounts}
				onShowUnitGuide={handleShowUnitGuide}
				nearMeParams={newNearMeParams}
			/>
		);
	} else if (isVehicleSerp) {
		filterComponent = <VehicleSearchFilters facetCounts={facetCounts} />;
	} else {
		filterComponent = (
			<SearchFilterContent
				onShowUnitGuide={handleShowUnitGuide}
				priceTransparency={priceTransparency}
				cityUrl={cityUrl}
				pageType={pageType}
				facetCounts={facetCounts}
			/>
		);
	}

	const isSearchRoute =
		match && match.url && match.url.includes('/search.html');

	const isNearMePage = pageType === 'storageNearMeLandingPage';

	const dealsDropdownValue = queryParams?.dealsFilter || 'all';

	// Helper function to determine the pagination search path
	const getPaginationSearchPath = () => {
		if (isStorageDealsPage) {
			return STORAGE_DEALS_SEARCH_PATH;
		}
		if (pageType === 'storageNearMeLandingPage' && match?.url) {
			return match.url.replace(/\.html$/, '') + '.html';
		}
		return searchPath;
	};
	const hasNonPaginationParams =
		isNearMePage &&
		urlQueryParams &&
		Object.keys(urlQueryParams).some((p) => p !== 'page');
	const shouldNoIndex =
		!!searchParams.informedSearch ||
		isSearchRoute ||
		hasNonPaginationParams;


	return (
		<Page
			pageType={pageType}
			className={classNames('search-page-variation')}
			id="search-page-variation-container"
		>
			{isSpinnerVisible && (
				<div className="search-page-variation__spinner">
					<Spinner show />
				</div>
			)}
			{shouldNoIndex && (
				<Helmet>
					<meta
						name="robots"
						content="noindex, nofollow"
					/>
				</Helmet>
			)}
			{!isSearchRoute && !searchParams.informedSearch && (
				<Helmet>
					<meta
						name="msvalidate.01"
						content="72430AA401040F456AF99A586F12B284"
					/>

					{!isNearMePage && clpPageSchema && (
						<script type="application/ld+json">
							{clpPageSchema}
						</script>
					)}
					{isNearMePage && nearMeWebpageSchema && (
						<script type="application/ld+json">
							{nearMeWebpageSchema}
						</script>
					)}
					{isNearMePage && nearMeWebSiteSchema && (
						<script type="application/ld+json">
							{nearMeWebSiteSchema}
						</script>
					)}
					{facilitySelfStorageSchemas &&
						facilitySelfStorageSchemas.map((schema, index) => (
							<script
								key={`facility-schema-${index}`}
								type="application/ld+json"
							>
								{schema}
							</script>
						))}
				</Helmet>
			)}

			<StickySearchBar
				handleSearch={handleSearch}
				searchLocation={searchLocation}
				queryParams={queryParams}
				onSearch={handleSearchParamChange}
				searchParams={searchParams}
				closeFilters={toggleFilters}
				filtersShowing={filtersShowing}
				onSearchParamChange={handleSearchParamChange}
				isLarge={isLarge(viewportWidth)}
				handleToggleFilters={handleToggleFilters}
				filterComponent={filterComponent}
				unitSizeGuideShowing={unitSizeGuideShowing}
				handleHideUnitGuide={handleHideUnitGuide}
				filtersCount={filterCount}
				primaryHeadline={primaryHeadline}
				isNearMePage={isNearMePage}
				searchType={searchType}
				nearMeSearchPath={isNearMePage ? getPaginationSearchPath() : undefined}
			/>

			<Modal
				showing={unitSizeGuideShowing}
				onClose={handleHideUnitGuide}
			>
				<ModalBody>
					<UnitSizeGuide />
				</ModalBody>
			</Modal>

			<div className="search-page-variation__content">
				<div
					className={classNames('search-container', mobileView)}
					id="search-container"
				>
					{!isLarge(viewportWidth) && filtersShowing && (
						<SearchFiltersTray
							handleSearch={handleSearch}
							handleToggleFilters={handleToggleFilters}
							queryParams={queryParams}
							onSearch={handleSearchParamChange}
							closeFilters={toggleFilters}
							filtersShowing={filtersShowing}
							onSearchParamChange={handleSearchParamChange}
							isLarge={isLarge(viewportWidth)}
							filtersCount={filterCount}
						>
							{filterComponent}
						</SearchFiltersTray>
					)}
					<Row>
						<Column
							large={6}
							xlarge={6}
							className="map-column"
						>
							<div className="map-sticky-container">
								<EmbeddedSearchMap
									searchLocation={searchLocation}
									facilities={facilities}
									facilitiesPerPage={facilitiesPerPage}
									totalFacilities={totalFacilities}
									page={page}
									preselectedFacilityId={
										preselectedFacilityId
									}
									queryParams={queryParams}
									highlightedFacility={highlightedFacility}
									onSearch={handleSearchParamChange}
									onMapReady={hideSpinner}
									onMapSearchStart={showSpinner}
									onScrollToFacility={handleScrollToFacility}
									searchParams={searchParams}
									searchType={searchType}
									baseUrl={baseUrl}
								/>
							</div>
						</Column>

						<Column
							large={6}
							xlarge={6}
							className="list-column"
						>
							{isNearMePage &&
								match?.params?.keyword === 'storage-deals' && (
									<DealsDropdown
										className="deals-dropdown"
										options={DEALS_DROPDOWN_OPTIONS}
										value={dealsDropdownValue}
										placeholder="All Deals"
										onChange={(dealsFilter) =>
											handleSearchParamChange({
												dealsFilter,
											})
										}
									/>
								)}

							<div className="pagination-wrapper">
								<PaginationSummary
									hideIfEmpty
									page={page}
									itemsPerPage={facilitiesPerPage}
									totalItems={totalFacilities}
									prefix={searchLocation.text}
									label={`Storage Facilities`}
									isDot={true}
									isNearMePage={isNearMePage}
								/>

								{useNewFilters && (
									<SortDropDownVariation
										onChange={handleSearchParamChange}
										value={
											queryParams?.order ||
											searchParams?.order
										}
									/>
								)}
							</div>

							<FacilityList
								facilities={facilities}
								showNoResults={!loading}
								baseUrl={baseUrl}
								searchParams={searchParams}
								storageType={searchType}
								withSchema={!isSearchRoute}
								highlightedFacility={highlightedFacility}
								vipFacilityId={vipFacilityId}
								firstListingIsVipEligible={
									firstListingIsVipEligible
								}
								searchTerm={searchTerm}
								pageType={pageType}
								match={match}
							/>
							{numPages > 1 && (
								<SearchPagination
									searchParams={searchParams}
									numPages={numPages}
									selectedPage={page}
									searchPath={getPaginationSearchPath()}
								/>
							)}
						</Column>
					</Row>
				</div>

				<Column>
					<div className="seo-content-below-fold">
						<SEOContentVariation
							phone={phone}
							getTollfreePhoneNumber={
								actions.getTollfreePhoneNumber
							}
							searchParams={searchParams}
							searchLocation={searchLocation}
							searchContent={searchContent}
							faqContent={faqContent}
							faqShowing={faqShowing}
							averageUnitPriceData={
								searchContent?.averageMonthlyUnitPrice
							}
							bestPricedUnits={units}
							canonicalUrl={canonicalUrl}
							cityUrl={cityUrl}
							stateUrl={stateUrl}
							isCLP={!isSearchRoute}
							baseUrl={baseUrl}
							amenity={searchParams?.amenity}
							primaryHeadline={primaryHeadline}
							nearMeKeyword={match?.params?.keyword}
							isNearMePage={isNearMePage}
						/>
					</div>
				</Column>

				<BackToTop />
			</div>

			<SearchCTCFooter
				withinCallCenterHours={withinCallCenterHours}
				mobileView={mobileView}
				handleViewListMap={toggleMobileView}
				onToggleListMap={toggleMobileView}
				onOpenFilters={toggleFilters}
				filtersCount={filterCount}
				phone={phone}
			/>

			{!isSearchRoute && !searchParams?.informedSearch && (
				<script
					key="schema"
					type="application/ld+json"
					// eslint-disable-next-line react/no-danger
					dangerouslySetInnerHTML={{ __html: clpMapSchema }}
				/>
			)}
		</Page>
	);
}

SearchPageVariation.propTypes = {
	actions: PropTypes.shape({
		getFacilityHoursAndDirection: PropTypes.func,
		getLocalPhoneNumberByZipCode: PropTypes.func,
		getLocalPhoneNumberByCityState: PropTypes.func,
		getTollfreePhoneNumber: PropTypes.func,
	}),
	toggleFilters: PropTypes.func,
	filtersShowing: PropTypes.bool,
	handleSearchParamChange: PropTypes.func,
	handleHideUnitGuide: PropTypes.func,
	isTruckSerp: PropTypes.bool,
	queryParams: PropTypes.object,
	showHoursForFacility: PropTypes.number,
	openHoursRequest: PropTypes.func,
	closeHoursRequest: PropTypes.func,
	unitSizeGuideShowing: PropTypes.bool,
	injectedComponents: PropTypes.object,
	injectAllEvenIfNoContent: PropTypes.bool,
	showVehicleLengthTool: PropTypes.bool,
	listType: PropTypes.oneOf(['grid', 'column']),
	viewportWidth: PropTypes.number.isRequired,
	searchParams: PropTypes.shape({
		subPageType: PropTypes.string,
		informedSearch: PropTypes.bool,
	}).isRequired,
	totalFacilities: PropTypes.number,
	searchLocation: PropTypes.object,
	searchId: PropTypes.string,
	handleSearch: PropTypes.func.isRequired,
	canonicalUrl: PropTypes.string,
	staticMapUrl: PropTypes.string,
	cityUrl: PropTypes.string,
	facilities: PropTypes.arrayOf(PropTypes.object),
	locationLabel: PropTypes.string,
	searchTerm: PropTypes.string,
	searchType: PropTypes.string,
	page: PropTypes.number.isRequired,
	facilitiesPerPage: PropTypes.number,
	loading: PropTypes.bool,
	localPhone: PropTypes.object,
	stateUrl: PropTypes.string,
	searchContent: PropTypes.object,
	numPages: PropTypes.number,
	phone: PropTypes.object,
	faqContent: PropTypes.arrayOf(PropTypes.object),
	faqShowing: PropTypes.bool,
	firstListingIsVipEligible: PropTypes.bool,
	vipAccount: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]),
	vipFacilityId: PropTypes.number,
	vipFacilityImages: PropTypes.arrayOf(
		PropTypes.shape({
			src: PropTypes.string,
			atl: PropTypes.string,
		}),
	),
	preselectedFacilityId: PropTypes.number,
	fmfFacilitiesCount: PropTypes.number,
	promoFacilitiesCount: PropTypes.number,
	pageType: PropTypes.string,
	clpPageSchema: PropTypes.string,
	clpMapSchema: PropTypes.string,
	nearMeWebpageSchema: PropTypes.string,
	nearMeWebSiteSchema: PropTypes.string,
	facilitySelfStorageSchemas: PropTypes.arrayOf(PropTypes.string),
	baseUrl: PropTypes.string,
	searchPath: PropTypes.string,
	units: PropTypes.arrayOf(
		PropTypes.shape({
			averagePrice: PropTypes.number,
			facility: PropTypes.number,
			facilityCity: PropTypes.string,
			facilityImage: PropTypes.string,
			facilityName: PropTypes.string,
			facilityState: PropTypes.string,
			inNetwork: PropTypes.bool,
			price: PropTypes.string,
			reserveUrl: PropTypes.string,
			sizeText: PropTypes.string,
			unit: PropTypes.number,
			squareFootage: PropTypes.number,
		}),
	),

	// Props used by the variation UI only
	filterComponent: PropTypes.node,
	mobileView: PropTypes.oneOf(['list', 'map']),
	toggleMobileView: PropTypes.func,
	highlightedFacility: PropTypes.number,
	isSpinnerVisible: PropTypes.bool,
	showSpinner: PropTypes.func,
	hideSpinner: PropTypes.func,
	withinCallCenterHours: PropTypes.bool,
	filterCount: PropTypes.number,
	handleToggleFilters: PropTypes.func,
	handleScrollToFacility: PropTypes.func,
	isStorageDealsPage: PropTypes.bool,
	primaryHeadline: PropTypes.string,
	prevUrl: PropTypes.string,
	nextUrl: PropTypes.string,
	match: PropTypes.object,
};

export default enhance(SearchPageVariation);
